import React, { useState } from 'react';
import { PageHeader } from '../components/PageBits';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (res.ok) {
        toast({ title: 'Message sent!', description: "Thank you for reaching out. The REFOCC team will get back to you shortly." });
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        toast({ title: 'Failed to send', description: data.error || 'Something went wrong. Please try again.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Network error', description: 'Could not reach the server. Please check your connection and try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <PageHeader kicker="Contact" title="Let's begin a" accent="conversation." intro="Whether you want to volunteer, partner, sponsor, invite REFOCC for a programme, or simply learn more, the team welcomes your message." />
      <section className="py-12 pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2 space-y-5">
            {[
              { icon: Mail, label: 'Email', val: 'info@refocc.org' },
              { icon: Phone, label: 'Phone', val: '+91 80892 01013' },
              { icon: MapPin, label: 'Address', val: 'D Square Road, Cheruppa - Kayalam Road, Mavoor, Kerala 673661' },
            ].map(x => (
              <div key={x.label} className="bg-white border border-refocc-dark/10 rounded-2xl p-5 flex gap-4">
                <div className="w-11 h-11 rounded-full bg-refocc-orange/15 text-refocc-orange flex items-center justify-center shrink-0"><x.icon size={18}/></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-refocc-dark/55">{x.label}</div>
                  <div className="text-refocc-dark font-medium mt-0.5">{x.val}</div>
                </div>
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <a href="https://www.facebook.com/refocc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-refocc-dark/15 flex items-center justify-center text-refocc-dark/60 hover:bg-refocc-orange hover:text-white hover:border-refocc-orange transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/refocc_/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-refocc-dark/15 flex items-center justify-center text-refocc-dark/60 hover:bg-refocc-orange hover:text-white hover:border-refocc-orange transition-colors">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
            </div>
          </div>
          <form onSubmit={submit} className="md:col-span-3 bg-white border border-refocc-dark/10 rounded-3xl p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" className="w-full border border-refocc-dark/15 rounded-xl px-4 py-3 bg-refocc-cream/50 focus:outline-none focus:border-refocc-orange transition-colors" />
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Your email" className="w-full border border-refocc-dark/15 rounded-xl px-4 py-3 bg-refocc-cream/50 focus:outline-none focus:border-refocc-orange transition-colors" />
            </div>
            <input required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Subject" className="w-full border border-refocc-dark/15 rounded-xl px-4 py-3 bg-refocc-cream/50 focus:outline-none focus:border-refocc-orange transition-colors" />
            <textarea required rows={6} value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Your message" className="w-full border border-refocc-dark/15 rounded-xl px-4 py-3 bg-refocc-cream/50 focus:outline-none focus:border-refocc-orange transition-colors resize-none" />
            <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-refocc-orange text-white px-6 py-3 rounded-full font-medium hover:bg-[#B97651] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {sending ? (
                <>Sending... <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" /></svg></>
              ) : (
                <>Send message <Send size={16}/></>
              )}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
